import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Form, { FieldWatch } from "@imminently/decisively-ui/Form";
import { decompress } from "compress-json";
import Dialog, { useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import Alert from "@imminently/decisively-ui/Alert";
import Icon from "@imminently/decisively-ui/Icon";
import SwitchField from "@imminently/decisively-ui/SwitchField";
import TextField from "@imminently/decisively-ui/TextField";
import { getSanitizedFields } from "@packages/commons";
import { releaseService } from "@/app/providers/services";
import { ExportSanitizationAlert } from "@/components/ExportSanitizationAlert";
import { useNotify } from "@/common/notifications";
import { setRelease } from "@/redux/scope";
import { type FileData, FileUpload } from "@/components/forms/hookForm/FileUpload";
import { importReleaseSchema, type ImportReleaseFormValues } from "../types";
import { buildImportReleasePayload } from "../utils";

// ─── File parser ─────────────────────────────────────────────────────────────

const parseRelease = (content: any) => {
  const parsed = JSON.parse(content);
  if (!parsed.compressed) throw new Error("Invalid export file");

  const decompressedValue = decompress(parsed.compressed);
  if (!decompressedValue.release) throw new Error("Invalid export file");
  const sanitizedFields = getSanitizedFields(decompressedValue);

  // Project export uploaded in a release import slot — warn but still allow (only the release is imported)
  if (decompressedValue.model) {
    return { ...parsed, _isProjectExport: true, sanitizedFields };
  }

  return { ...parsed, sanitizedFields };
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ImportReleaseModalProps {
  projectId: string;
}

export const ImportReleaseModal = ({ projectId }: ImportReleaseModalProps) => {
  const { t } = useTranslation();
  const dialogContext = useDialogContext();
  const dispatch = useDispatch();
  const notify = useNotify();

  const { mutateAsync, isLoading } = releaseService.usePostCustom("import");

  const onSubmit = async (data: ImportReleaseFormValues) => {
    if (!data.file) {
      notify.error("No file selected");
      return;
    }

    const payload = buildImportReleasePayload(projectId, data);

    try {
      const res = await mutateAsync(payload);
      releaseService.invalidateAllQueries();
      notify.info("Release imported successfully");
      if (data.active && res?.id) {
        dispatch(setRelease(res.id));
      }
      dialogContext?.close();
    } catch {
      notify.error("Failed to import release");
    }
  };

  return (
    <Dialog.Content className="flex flex-col">
      <DialogHeader
        title={t("release_centre.import_release")}
        description="Import a previously exported release file."
      />

      <Form
        schema={importReleaseSchema}
        defaultValues={{
          file: null,
          active: true,
          preserveConnections: true,
          preserveVariables: true,
          preserveDerivedTables: false,
          preserveMappings: false,
          description: "",
          releaseNo: "",
        }}
        onSubmit={onSubmit}
        className="flex flex-col"
      >
        <Dialog.Body asChild>
          <FormPanel className="scrollable">
            <FileUpload
              field="file"
              parseFile={parseRelease}
            />
            <FieldWatch names={["file"]}>
              {({ file }) => (
                <>
                  {file?.data?._isProjectExport ? (
                    <Alert
                      variant="warning"
                      icon={<Icon name="warning" />}
                      description="This looks like a project export. Only the release will be imported — to also create the project, use Import Project instead."
                    />
                  ) : null}
                  {file?.data?.sanitizedFields?.length ? (
                    <ExportSanitizationAlert fields={file.data.sanitizedFields} />
                  ) : null}
                </>
              )}
            </FieldWatch>

            <SwitchField
              name="active"
              label="Make Current"
              helperText="When enabled, the imported release will be set as the active (current) release. The previously active release will be deactivated."
            />

            <SwitchField
              name="preserveConnections"
              label="Preserve Connections"
              helperText="When enabled, connections from the active release will be merged with connections from the imported release."
            />

            <SwitchField
              name="preserveVariables"
              label="Preserve Variables"
              helperText="When enabled, variables from the active release will be merged with variables from the imported release."
            />

            <SwitchField
              name="preserveDerivedTables"
              label="Preserve Derived Tables"
              helperText="When enabled, derived tables from the active release will be merged with derived tables from the imported release."
            />

            <SwitchField
              name="preserveMappings"
              label="Preserve Mapping"
              helperText="When enabled, mapping from the active release will be merged with mapping from the imported release."
            />

            <TextField
              name="description"
              label="Description"
              placeholder="Optional description for this imported release"
              fullWidth
            />

            <TextField
              name="releaseNo"
              label="Override Release Number"
              type="number"
              placeholder="Optional - leave blank to auto-calculate"
              helperText="Override the release number for this import. If set, this number will be used instead of automatically calculating the next release number. Warning: this can result in multiple releases having the same number."
              fullWidth
            />
          </FormPanel>
        </Dialog.Body>

        <Dialog.Actions>
          <FieldWatch names={["file"]}>
            {({ file }) => (
              <Dialog.Actions.PrimaryButton
                label="Import"
                type="submit"
                loading={isLoading}
                disabled={isLoading || !file}
              />
            )}
          </FieldWatch>
        </Dialog.Actions>
      </Form>
    </Dialog.Content>
  );
};
